import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../utils/formatters';
import { Printer } from 'lucide-react';
import { printContent } from '../../../utils/print';

export default function RelatorioRomaneio() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [filters, setFilters] = useState({ rota_id: '', turno: 'Manhã', data: new Date().toISOString().split('T')[0] });
  const [rotas, setRotas] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef(null);

  useEffect(() => { api.get('/rotas').then(r => setRotas(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const gerarRomaneio = async () => {
    if (!filters.rota_id || !filters.turno || !filters.data) { setError('Selecione rota, turno e data'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/relatorios/romaneio?rota_id=${filters.rota_id}&turno=${filters.turno}&data=${filters.data}`);
      setData(res.data);
    } catch (err) { setError(err.userMessage || 'Erro ao gerar romaneio'); }
    finally { setLoading(false); }
  };

  const inputCls = "rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid";

  return (
    <div data-testid="relatorio-romaneio">
      <div className="flex items-center justify-between mb-6">
        <h1 className="dashboard-heading text-2xl">Romaneio de Entrega</h1>
        {data && <button onClick={() => printContent('Romaneio de Entrega', printRef)} className="no-print flex items-center gap-2 py-2 px-4 rounded-lg btn-primary font-dashboard font-semibold text-sm" data-testid="print-btn"><Printer className="w-4 h-4" /> Imprimir</button>}
      </div>

      <div className="card-dashboard mb-6 no-print">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Rota *</label>
            <select className={`w-full ${inputCls}`} value={filters.rota_id} onChange={e => set('rota_id', e.target.value)} data-testid="select-rota">
              <option value="">Selecione...</option>
              {rotas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Turno *</label>
            <select className={`w-full ${inputCls}`} value={filters.turno} onChange={e => set('turno', e.target.value)}>
              <option>Manhã</option><option>Tarde</option>
            </select>
          </div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Data *</label>
            <input type="date" className={`w-full ${inputCls}`} value={filters.data} onChange={e => set('data', e.target.value)} />
          </div>
        </div>
        <button onClick={gerarRomaneio} disabled={loading} className="py-2.5 px-6 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-60" data-testid="gerar-romaneio-btn">
          {loading ? 'Gerando...' : 'Gerar Romaneio'}
        </button>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      </div>

      {data && (
        <div ref={printRef} className="print-content">
          {/* Header */}
          <div className="flex justify-between items-start mb-6 pb-4 border-b-2 border-brand-dark">
            <div>
              <h2 className="font-dashboard font-bold text-xl text-brand-text">{data.empresa?.nome_fantasia || 'Fábrica de Pães'}</h2>
              <p className="text-sm font-body text-brand-text-muted">Romaneio de Entrega</p>
            </div>
            <div className="text-right text-sm font-body text-brand-text-muted">
              <p>Data: <strong>{filters.data}</strong></p>
              <p>Rota: <strong>{data.rota?.nome}</strong></p>
              <p>Turno: <strong>{data.turno}</strong></p>
              <p>Entregador: <strong>{data.entregador?.nome || '–'}</strong></p>
            </div>
          </div>

          {/* Clients */}
          {data.pedidos?.map((p, i) => (
            <div key={p.id} className="page-break-avoid mb-6 border border-brand-border rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-dashboard font-bold text-brand-text">{i + 1}. {p.cliente_nome}</p>
                  <p className="text-xs text-brand-text-muted font-body">
                    {p.endereco_completo?.logradouro}, {p.endereco_completo?.numero} — {p.endereco_completo?.bairro}, {p.endereco_completo?.cidade}
                  </p>
                  {p.endereco_completo?.ponto_referencia && <p className="text-xs text-brand-text-muted font-body italic">Ref: {p.endereco_completo.ponto_referencia}</p>}
                  <p className="text-xs text-brand-primary font-body mt-0.5">Pedido #{p.numero}</p>
                </div>
                {isAdmin && p.total != null && <p className="font-dashboard font-bold text-brand-text">{formatCurrency(p.total)}</p>}
              </div>
              <table className="w-full text-sm font-body">
                <thead><tr className="border-b border-brand-border"><th className="text-left py-1 text-xs font-dashboard">Produto</th><th className="text-center py-1 text-xs font-dashboard">Qtd</th><th className="text-center py-1 text-xs font-dashboard">Un</th></tr></thead>
                <tbody>
                  {p.itens?.map((item, j) => (
                    <tr key={j} className="border-b border-brand-border last:border-0">
                      <td className="py-1">{item.descricao}</td>
                      <td className="py-1 text-center font-bold">{item.quantidade}</td>
                      <td className="py-1 text-center text-brand-text-muted">{item.unidade}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 pt-3 border-t border-dashed border-brand-border">
                <p className="text-xs text-brand-text-muted font-body">Assinatura: ___________________________________</p>
              </div>
            </div>
          ))}

          {/* Footer */}
          <div className="border-t-2 border-brand-dark pt-4 flex justify-between text-sm font-body">
            <div>
              <p>Total de clientes: <strong>{data.total_clientes}</strong></p>
              <p>Total de itens: <strong>{data.total_itens}</strong></p>
            </div>
            {isAdmin && data.total_geral != null && <p className="font-dashboard font-bold text-lg text-brand-text">Total: {formatCurrency(data.total_geral)}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
